import { useState } from 'react';
import { Bell, MessageCircle, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { priceAlerts } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const WINDOWS = [
  { days: 7, label: '1 week' },
  { days: 10, label: '10 days' },
  { days: 15, label: '15 days' },
  { days: 30, label: '1 month' },
];

/**
 * NotifyPriceDropButton — customer-only "ring me when the price drops" button.
 * Pops a small dialog with window choice (7/10/15/30 days) + SMS / WhatsApp toggles.
 */
export default function NotifyPriceDropButton({ productQuery, testId = 'notify-price-drop-btn' }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [window, setWindow] = useState(15);
  const [sms, setSms] = useState(true);
  const [wa, setWa] = useState(true);
  const [busy, setBusy] = useState(false);

  if (!user || user.role !== 'customer') return null;

  const submit = async () => {
    setBusy(true);
    try {
      await priceAlerts.create({
        product_query: productQuery,
        window_days: window,
        notify_sms: sms,
        notify_whatsapp: wa,
      });
      toast.success(`We'll ping you on price drops over the next ${window} days.`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create alert');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid={testId}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#FFF3E0] hover:bg-[#FFE0B2] text-[#9A4500] border border-[#FFCC80] px-3 py-1.5 text-xs font-bold transition"
      >
        <Bell className="w-3.5 h-3.5"/> Notify me on price drop
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Get pinged when "{productQuery}" gets cheaper</DialogTitle>
            <DialogDescription>
              We'll watch every store for you and ping the moment the price drops.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-2">Watch window</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {WINDOWS.map(w => (
                <button
                  key={w.days}
                  data-testid={`notify-window-${w.days}`}
                  onClick={() => setWindow(w.days)}
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${window === w.days ? 'border-[#0D47A1] bg-[#E3F2FD] text-[#0D47A1]' : 'border-border bg-white text-ink/70 hover:border-[#0D47A1]/40'}`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-2">Ping me on</div>
            <div className="flex gap-2 flex-wrap">
              <label className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-2 cursor-pointer">
                <input data-testid="notify-channel-sms" type="checkbox" checked={sms} onChange={e => setSms(e.target.checked)} className="accent-[#0D47A1]"/>
                <Smartphone className="w-4 h-4 text-[#0D47A1]"/>
                <span className="text-sm font-semibold">SMS</span>
              </label>
              <label className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-2 cursor-pointer">
                <input data-testid="notify-channel-whatsapp" type="checkbox" checked={wa} onChange={e => setWa(e.target.checked)} className="accent-[#25D366]"/>
                <MessageCircle className="w-4 h-4 text-[#25D366]"/>
                <span className="text-sm font-semibold">WhatsApp</span>
              </label>
            </div>
            <div className="text-[11px] text-ink/50 mt-1.5">SMS &amp; WhatsApp are being wired up — until then you'll always see the alert in your notifications and on <b>My Price Alerts</b>.</div>
          </div>

          <button
            data-testid="notify-submit-btn"
            disabled={busy || (!sms && !wa)}
            onClick={submit}
            className="btn-primary w-full mt-5"
          >
            <Bell className="w-4 h-4"/>{busy ? 'Saving…' : `Watch for ${window} day${window === 1 ? '' : 's'}`}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
